<template>
  <div
    v-if="modalVisible && currentStudentDetail"
    id="student-notation-modal"
    @keydown.escape="closeCard(false)"
  >
    <div class="cache" @click="closeCard(false)"/>
    <transition name="fade" appear>
      <div class="modal-container">
        <div class="close" @click="closeCard(false)">
          <i class="fa fa-times" aria-hidden="true"/>
        </div>
        <div class="content-container">
          <div class="info-container">
            <div v-if="currentStudentDetail.mainPicture">
              <SingleAvatarStudent
                :picture="currentStudentDetail.mainPicture"
                size="108px"
                :gender="currentStudentDetail.gender"
                rounded="false"
              />
            </div>
            <div v-else>
              <SingleAvatarStudent
                size="108px"
                font-size="60px"
                rounded="false"
                :gender="currentStudentDetail.gender"
                :short-name="currentStudentDetail.shortName"
              />
            </div>
            <div class="info">
              <div class="name">{{ currentStudentDetail.firstName }} {{ currentStudentDetail.lastName }}
                <span v-if="currentStudentDetail.birthdayDate">{{ dateOfBirth(currentStudentDetail.birthdayDate) }} ans
                </span>
              </div>
              <div v-if="noteIsDisabled && !currentNote" class="rating-container">
                <div class="rate-comment" style="margin-left: 10px">
                  {{ $t('student_modal.detail_noscore_comment') }}
                </div>
              </div>
              <div v-if="!noteIsDisabled && currentStudentDetail" class="rating-container">
                <div class="notation">
                  <el-rate
                    id="modal-rate"
                    v-model="currentNote"
                    :disabled="noteIsDisabled"
                    void-icon="StarFilled"
                    void-color="#e5e6ea"
                    :colors="['#ffd859','#ffd859','#ffd859']"
                    @change="changeNoteOnModal"
                  />
                  <div class="rate-comment">{{ rateComment(currentNote) }}</div>
                </div>
                <div v-if="currentNote && ratedSoftskills && ratedSoftskills.length !== 0" class="softskills">
                  <el-tooltip
                    effect="dark"
                    :content="getSoftSkillsNames(ratedSoftskills)"
                    placement="top"
                    :show-arrow="false"
                  >
                    <div class="with-rating item">
                      <span class="sub-icon-text" :class="{ warning: currentNote === 2 || currentNote === 1 || currentNote === 3 }"> {{ ratedSoftskills[0].name }}</span>
                      <span
                        v-if="ratedSoftskills[1]"
                        class="sub-icon-text"
                        :class="{ warning: currentNote === 2 || currentNote === 1 || currentNote === 3 }"
                      >{{ ratedSoftskills[1].name }}</span>
                      <span
                        v-if="ratedSoftskills[2]"
                        class="sub-icon-text"
                        :class="{ warning: currentNote === 2 || currentNote === 1 || currentNote === 3 }"
                      >+{{ ratedSoftskills.length - 2 }} <i class="fa fa-info-circle"/></span>
                    </div>
                  </el-tooltip>
                </div>
              </div>
            </div>
          </div>
          <div class="detail-container">
            <transition v-if="loadingDetails" name="fade">
              <div class="container-loading-no-sidebar">
                <div class="loading">
                  <div class="loader"/>
                </div>
              </div>
            </transition>
            <StudentModalDetails
              :current-student-detail="currentStudentDetail"
              :current-client="currentClient"
              :loading-details="loadingDetails"
            />
            <CarouselFeedbacksStudent
              :visible="!loadingDetails"
              :feedbacks="carouselFeedbacks"
            />

            <StudentOnTopPopup
              v-if="showOnTopPopup"
              :student="currentStudentDetail"
              :soft-skill-list="softskillsList"
              :current-note="currentNote"
              :preload-soft-skills="preloadSoftSkills"
              @save-feedback="saveFeedback"
              @softskills-selected="onSelectedSoftskills"
            />
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import studentService from '@/services/students/';
import moment from 'moment';
import SingleAvatarStudent from '@/components/MyStudents/SingleAvatarStudent';
import CarouselFeedbacksStudent from '@/components/Global/CarouselFeedbacksStudent';
import tippy from 'tippy.js';
import { mapStores } from 'pinia';
import { useCompanyStore } from '@/stores/company';
import StudentOnTopPopup from './StudentOnTopPopup';
import StudentModalDetails from './StudentModalDetails';

export default {
  components: {
    StudentOnTopPopup,
    StudentModalDetails,
    SingleAvatarStudent,
    CarouselFeedbacksStudent,
  },
  props: ['currentStudent', 'modalVisible', 'note', 'lastNoteBeforeDrop', 'edited', 'noteIsDisabled', 'token', 'fromUserId', 'preloadSoftSkills', 'studentValidation'],
  data() {
    return {
      // `edited` est une prop : la noter depuis la modale ne peut pas l'écrire
      // en retour, d'où ce drapeau local qui prend le relais.
      editedInModal: false,
      carouselFeedbacks: null,
      softskillsList: [],
      ratedSoftskills: [],
      currentStudentId: null,
      currentUserJobId: null,
      currentNote: null,
      currentStudentDetail: null,
      lastNote: null,
      lastNoteTemp: null,
      loadingDetails: false,
      showOnTopPopup: false,
      feedbackSaved: false,
      feedbacks: null,
      starText: [
        '&#x1F616 <span class="star-text-separator">|</span> Mauvaise attitude',
        '&#x1F61F <span class="star-text-separator">|</span> On ne veut plus',
        '&#x1F615 <span class="star-text-separator">|</span> On évite',
        '&#x1F60A <span class="star-text-separator">|</span> On aime',
        '&#x1F60D <span class="star-text-separator">|</span> Top priorité',
      ],
    };
  },
  computed: {
    ...mapStores(useCompanyStore),
    hasBeenEdited() {
      return this.edited || this.editedInModal;
    },
    currentClient() {
      return this.companyStore.nameCompany;
    },
  },
  watch: {
    modalVisible(visible) {
      if (visible) {
        this.resetModal();
        this.lastNoteTemp = this.lastNoteBeforeDrop ? this.lastNoteBeforeDrop : null;
        this.currentStudentId = this.currentStudent.id;
        if (this.currentStudent.userId) {
          this.currentStudentId = this.currentStudent.userId;
          this.currentUserJobId = this.currentStudent.id;
        }

        if (this.studentValidation) {
          this.currentUserJobId = this.studentValidation.id;
        }
        this.openCard();
      }
    },
  },
  mounted() {
  },
  methods: {
    rateComment(rate) {
      rate = parseInt(rate, 10);
      switch (rate) {
        case 0:
          return `Noter ce ${this.$brand.workerLower}`;
        case 1:
          return 'Mauvaise attitude';
        case 2:
          return 'On ne veut plus';
        case 3:
          return 'On évite';
        case 4:
          return 'On aime';
        case 5:
          return 'Top priorité';
        default:
      }
      return rate;
    },
    dateOfBirth(date) {
      return moment()
        .diff(date, 'years');
    },

    fetchStudentDetail() {
      this.loadingDetails = true;
      studentService.detail(this.currentStudentId, (details) => {
        this.currentStudentDetail = details;
        this.carouselFeedbacks = details.lastTenFeedbacks;
        this.ratedSoftskills = details.softSkillsRatedByCustomer;
        this.lastNote = details.feedbackScoreForCurrentCustomer;
        this.loadingDetails = false;
        if (this.hasBeenEdited) {
          this.openSoftSkillsModal();
        }

        // tooltip on top of star icons
        if (!this.noteIsDisabled) {
          this.$nextTick(() => {
            const el = document.getElementById('modal-rate');
            // The modal may have been closed before the details request resolved.
            if (!el) {
              return;
            }
            for (let i = 0; i < 5; i += 1) {
              tippy(el.children[i].children[0], {
                content: this.starText[i],
                placement: 'top',
                arrow: true,
              });
            }
          });
        }
      });
    },
    fetchSoftSkills() {
      this.loadingDetails = true;
      studentService.getSoftSkills(this.currentStudentId)
        .then((response) => {
          this.softskillsList = response;
        });
    },
    onSelectedSoftskills(selectedSoftskills) {
      this.ratedSoftskills = selectedSoftskills;
    },
    openCard() {
      if (this.hasBeenEdited) {
        this.currentNote = this.note;
      } else if (this.currentStudent.feedbackScoreForCurrentCustomer) {
        this.currentNote = this.currentStudent.feedbackScoreForCurrentCustomer;
      }
      this.fetchSoftSkills();
      this.fetchStudentDetail();
    },
    closeCard(feedbackSaved) {
      if (this.loadingDetails) {
        return;
      }

      if (this.hasBeenEdited && feedbackSaved === false) {
        const confirmeed = window.confirm('Si vous fermez la fenêtre, vote avis ne sera pas sauvegardé.\nPour sauvegarder votre avis, veuillez cliquer sur le bouton envoyer en bas de cette page.\nÊtes-vous sûr de vouloir fermer ?');
        if (confirmeed) {
          this.revertList();
        } else {
          return;
        }
      }

      this.$emit('close-modal', false);
    },
    resetModal() {
      this.editedInModal = false;
      this.feedbackSaved = null;
      this.currentStudentDetail = null;
      this.showOnTopPopup = false;
      this.ratedSoftskills = false;
      this.currentNote = null;
    },
    revertList() {
      this.$emit('revert-list', this.currentStudent);
    },
    mxNoteStudent(params) {
      let from = 'From Student Board';
      if (this.$route.name === 'job.index') {
        from = 'From Job Detail';
      }

      if (this.$route.name === 'job.feedback') {
        from = 'From Landing Page';
      }
      const config = {
        From: from,
        'New Note': this.currentNote,
      };
      if (this.lastNoteTemp && this.lastNoteTemp === 0) {
        config['Old Note'] = this.lastNoteTemp;
      } else if (this.lastNote) {
        config['Old Note'] = this.lastNote;
      }
      config['Soft Skills'] = params.selectedSoftskills ? params.selectedSoftskills.map((obj) => obj.name) : null;
      config['Has Comment'] = !!params.comment;
      this.$mixpanel.track('Notation Student', config);
    },
    changeNoteOnModal(note) {
      this.currentNote = note;
      this.openSoftSkillsModal();
      this.editedInModal = true;
    },
    saveFeedback(params) {
      this.feedbacks = params;
      this.showOnTopPopup = false;
      this.editedInModal = true;
      this.$notify.success({
        title: this.$t('toast_message.notation.register_note_to_student_success.title'),
        duration: 4000,
        message: this.$t(this.getTransationKeyForFeedbackSuccess(params), { PrenomStudent: this.currentStudentDetail.firstName }),
      });
      this.feedbackSaved = true;
      this.$emit('feedback-saved', params ? params.selectedSoftskills : null, this.currentNote);
      this.saveFeedbackAsync(params);
      this.closeCard(true);
    },
    openSoftSkillsModal() {
      this.showOnTopPopup = true;
    },
    async saveFeedbackAsync(params) {
      if (!this.hasBeenEdited) {
        return;
      }

      if (!this.currentNote) {
        return;
      }

      if (this.currentNote < 3 && (!params || !params.comment || params.comment === '')) {
        this.revertList();
        return;
      }

      this.$emit('save-list', this.currentStudent, this.currentNote);
      this.mxNoteStudent(params);
      const status = await studentService.updateStudentSoftskills(this.currentStudentDetail.id, this.token, {
        softskills: params ? params.selectedSoftskills : null,
        score: this.currentNote,
        comment: params ? params.comment : null,
        user_job_id: this.currentUserJobId,
      }, this.fromUserId);
      if (status !== 200) {
        this.feedbackSaved = false;
        this.revertList();
        this.$notify.error({
          title: '😔Ouuups',
          duration: 4000,
          message: "Une erreur est survenue, la note n'a pas été sauvegardée, Veuillez réessayer",
        });
      }
    },
    getTransationKeyForFeedbackSuccess(params) {
      if (this.currentNote < 4) {
        return 'toast_message.notation.register_note_to_student_success_with_comment.text';
      }

      if (params.selectedSoftskills && params.selectedSoftskills.length > 0) {
        return 'toast_message.notation.register_note_to_student_success_with_softskills.text';
      }
      return 'toast_message.notation.register_note_to_student_success.text';
    },
    getSoftSkillsNames(softSkills) {
      if (!softSkills) {
        return '';
      }
      return softSkills.reduce((acc, softSkill) => {
        acc.push(softSkill.name);
        return acc;
      }, []).join(', ');
    },
  },
};
</script>
